import { updateTenant } from "../api";
import {
  Title,
  TextInput,
  Button,
  Container,
  Center,
  Loader,
  Paper,
  Group,
  Text,
  Select,
  Code,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Link, useLocation } from "wouter";
import LedgerTable from "./LedgerTable";
import AddCreditsForm from "./AddCreditsForm";
import CreditsTable from "./CreditsTable";
import { useTenantStore } from "./tenantStore";
import { IconRefresh } from "@tabler/icons-react";
import { useEffect } from "react";

// `plans` lets each app pass its own tier names (the dropdown is shared across apps).
// Defaults to the generic boilerplate set when an app doesn't supply one.
export default function TenantEdit({
  params,
  plans = ["Free", "Starter", "Growth", "Pro", "Enterprise"],
  formatCreditType,
  creditsHint,
  extra,
}) {
  const [, setLocation] = useLocation();
  const tenant = useTenantStore((state) => state.tenants[params.id]);
  const fetchTenant = useTenantStore((state) => state.fetchTenant);
  const isLoading = useTenantStore((state) => state.isTenantLoading(params.id));
  const error = useTenantStore((state) => state.getTenantError(params.id));

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: tenant?.name || "",
      plan: tenant?.plan || "",
      creditAutoRefillThreshold: tenant?.creditAutoRefillThreshold || null,
      creditAutoRefillType: tenant?.creditAutoRefillType || "default",
    },
  });

  // Fetch tenant data if needed when component mounts
  useEffect(() => {
    if (!tenant) {
      fetchTenant(params.id)
        .then((data) => {
          const values = {
            name: data.name || "",
            plan: data.plan || "",
            creditAutoRefillThreshold: data.creditAutoRefillThreshold || null,
            creditAutoRefillType: data.creditAutoRefillType || "default",
          };
          form.setValues(values);
          form.setInitialValues(values);
        })
        .catch((err) => console.error("Failed to load tenant:", err));
    }
  }, [fetchTenant, params.id, tenant]);

  if (isLoading || (!tenant && !error)) {
    return (
      <Container size="sm">
        <Center>
          <Loader mt="xl" />
        </Center>
      </Container>
    );
  }

  if (error || !tenant) {
    return (
      <Container size="sm">
        <Center>
          <Text c="red">{error}</Text>
          <Button onClick={() => fetchTenant(params.id)}>Try Again</Button>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="sm">
      <Group justify="space-between">
        <Title order={1}>{tenant.name}</Title>

        <Group>
          <Button
            variant="default"
            component={Link}
            to={`/admin/users?tenantId=${params.id}`}
          >
            Users
          </Button>
          <Button
            leftSection={<IconRefresh />}
            onClick={() => fetchTenant(params.id, true)}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      <Paper my="md" withBorder p="md">
        <CreditsTable credits={tenant.credits} formatCreditType={formatCreditType} />

        <Title mt="md" order={4}>
          Adjust Credits
        </Title>
        <AddCreditsForm
          tenant={tenant}
          formatCreditType={formatCreditType}
          creditsHint={creditsHint}
        />
      </Paper>

      {extra}

      <Paper my="md" withBorder p="md">
        <form
          onSubmit={form.onSubmit((values) => {
            updateTenant(params.id, values).then((data) => {
              setLocation("/tenants");
            });
          })}
        >
          <TextInput
            label="Tenant Name"
            placeholder="ABC SAS"
            {...form.getInputProps("name")}
            key={form.key("name")}
            radius="md"
          />

          <Title mt="md" order={4}>
            Plan: {tenant.plan}
          </Title>
          <Select
            {...form.getInputProps("plan")}
            key={form.key("plan")}
            data={plans}
            description=" WARNING: Changing this ONLY changes the value in the database. It has no
        effect on factual credits, intervals, the Stripe plan, etc. and might
        get overwritten by the next webhook call. Only use this when you know
        what you're doing.
"
          />

          <Button mt="lg" type="submit">
            Save
          </Button>
        </form>
      </Paper>

      <Paper my="md" withBorder p="md">
        <Title order={4} mb="md">
          Auto-Refill Configuration
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          When enabled, the tenant's credit balance will be automatically topped
          up to the threshold at each billing cycle.
        </Text>
        <form
          onSubmit={form.onSubmit((values) => {
            const payload = {
              ...values,
              creditAutoRefillThreshold: values.creditAutoRefillThreshold
                ? parseInt(values.creditAutoRefillThreshold)
                : null,
            };
            updateTenant(params.id, payload).then((data) => {
              fetchTenant(params.id, true);
            });
          })}
        >
          <TextInput
            label="Refill Threshold"
            placeholder="10000"
            description="Credits will be topped up to this amount each billing cycle"
            type="number"
            {...form.getInputProps("creditAutoRefillThreshold")}
            key={form.key("creditAutoRefillThreshold")}
          />

          <Select
            label="Credit Type"
            description="Which credit type to refill (must match a configured 1-unit price)"
            {...form.getInputProps("creditAutoRefillType")}
            key={form.key("creditAutoRefillType")}
            data={["default", "premium", "standard"]}
            mt="md"
          />

          <Group mt="md">
            <Button type="submit">Save Auto-Refill Settings</Button>
            {tenant?.creditAutoRefillThreshold && (
              <Button
                variant="outline"
                color="red"
                onClick={() => {
                  form.setFieldValue("creditAutoRefillThreshold", null);
                  const payload = {
                    name: tenant.name,
                    plan: tenant.plan,
                    creditAutoRefillThreshold: null,
                    creditAutoRefillType: null,
                  };
                  updateTenant(params.id, payload).then(() => {
                    fetchTenant(params.id, true);
                  });
                }}
              >
                Disable Auto-Refill
              </Button>
            )}
          </Group>

          {tenant?.creditAutoRefillThreshold && (
            <Text mt="md" size="sm" c="teal">
              ✓ Auto-refill enabled: Will maintain{" "}
              {tenant.creditAutoRefillThreshold} {tenant.creditAutoRefillType}{" "}
              credits
            </Text>
          )}
        </form>
      </Paper>

      <Paper my="md" withBorder p="md">
        <Text>
          Stripe customer id:{" "}
          <Code>{tenant.claims?.stripeCustomerId ?? "NONE"}</Code>
        </Text>
      </Paper>

      <Paper my="xl">
        <Title order={3}>Last Credit Additions</Title>
        <LedgerTable ledger={tenant.ledgerAdditions} />
      </Paper>

      <Paper my="xl">
        <Title order={3}>Last Transactions</Title>
        <LedgerTable ledger={tenant.ledger} />
      </Paper>
    </Container>
  );
}
